'use client';
import './productCard.scss';

import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import { Box, Card, CardContent, CardMedia, Typography, Stack, Divider } from '@mui/material';
/* eslint-disable @next/next/no-img-element */
/* import libraries */
import { useState } from 'react';

import orderType from '@/constants/enums/orderType';
import { uiArchiveElementsValueMapper } from '@/lib/utils';
import { CartItemCardData } from './cartInterfaces';
import DeleteDialog from './dialog/deleteDialog';
import AddOrderName from './addOrderName';
import { useTranslation } from 'react-i18next';
import ElevationBg from '@/assets/img/elevation-bg.svg';
import SarBg from '@/assets/img/sar-bg.svg';

interface CartItemProps {
  type: orderType;
  cardData: CartItemCardData;
  overlap?: string | null;
  overlapData?: CartItemCardData[] | [];
  id: string;
}

export default function NewCartProduct({
  type,
  cardData,
  overlap,
  overlapData,
  id,
}: CartItemProps) {
  const [imageUrl, setImageUrl] = useState<string>(cardData?.thumbnailImg ?? '');
  const [isTextField, setIsTextField] = useState<boolean>(false);
  const { t } = useTranslation('cart');

  const itemId = cardData?.id ?? cardData?.uu_id ?? '';

  const elementsWithValueData = uiArchiveElementsValueMapper(
    cardData?.originalData,
    cardData?.cartDrawerElements,
    t,
    'cart_drawer',
    cardData?.locationName
  );
  const iconMapper: any = {
    aerial: (
      <span className="dt-icons-cart">
        <i className="icon-aerial-bg"></i>
      </span>
    ),
    elevation: <img src={ElevationBg.src} className="dt-icons-cart" />,
    sar: <img src={SarBg.src} className="dt-icons-cart" />,
    optical: (
      <span className="dt-icons-cart">
        <i className="icon-satellite-bg"></i>
      </span>
    ),
  };
  const getIcon = (icon: any) => {
    return iconMapper[icon];
  };
  const lowerCaseSensorType = cardData?.sensorType?.toLowerCase() || '';

  return (
    <Card className="product-card-cart product-card">
      <AddOrderName
        isTextField={isTextField}
        setIsTextField={setIsTextField}
        cardData={cardData}
        overlap={overlap ?? undefined}
        id={id}
        className="cart-drawer-add-order-name"
      />
      <Box className="card-content-wrapper">
        <Box className="img-wrapper new-icon">
          {type === orderType.NEW &&
          !Object.prototype.hasOwnProperty.call(cardData, 'thumbnailImg') ? (
            <Box className="img-thumbnail" aria-label="order-new-image-thumbnail"></Box>
          ) : imageUrl === '' ? (
            <Box className="img-wrapper">
              <Box className="img-thumbnail" aria-label="order-new-image-thumbnail"></Box>
              {overlap && <i className="icon-combine"></i>}
              {!overlap && getIcon(lowerCaseSensorType || '')}
            </Box>
          ) : (
            <>
              <CardMedia
                component="img"
                image={imageUrl}
                aria-label="existing-order-image"
                alt="Cart Item Picture"
                onError={(e: any) => {
                  e.target.onerror = null;
                  setImageUrl('');
                }}
              />
              {/* multi image icon here */}

              {overlap && <i className="icon-combine"></i>}
              {!overlap && getIcon(lowerCaseSensorType || '')}
            </>
          )}
          {type === orderType.NEW && <SatelliteAltOutlinedIcon aria-label="order-new-image-icon" />}
        </Box>
        <CardContent>
          <Box className="card-info-wrapper">
            <Box className="card-info">
              <Stack direction="column" spacing={0.5}>
                {elementsWithValueData &&
                  elementsWithValueData?.cartDrawerColumn1Elements?.map((item: any) => {
                    return (
                      <Typography key={item?.name} variant="caption" display="block">
                        {item?.value}
                      </Typography>
                    );
                  })}
              </Stack>
            </Box>
            <Box className="card-info">
              <DeleteDialog itemId={overlap ? id : itemId} />
              {elementsWithValueData &&
                elementsWithValueData?.cartDrawerColumn2Elements?.map((item: any) => {
                  return (
                    <Typography key={item?.name} variant="h6">
                      {item?.value}
                    </Typography>
                  );
                })}
            </Box>
          </Box>
        </CardContent>
      </Box>
      {overlap && (
        <Box sx={{ marginTop: '5px' }}>
          <Typography variant="caption">{`${overlapData?.length} ${overlapData && overlapData?.length > 1 ? t('images') : t('image')}`}</Typography>
        </Box>
      )}
      <Divider sx={{ marginTop: '8px', color: '#F4F6F5' }} />
    </Card>
  );
}
